import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, themeQuartz } from "ag-grid-community";
import { useCallback, useMemo, useState, Suspense } from "react";
import { Currency } from "@/gql/graphql";
import dayjs from "dayjs";
import {
  useGqlMutation,
  useGqlQuery,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  ReportReceiptItem,
  reportReceiptsQuery,
} from "./GraphQL/reportReceiptsQuery";
import {
  deleteReceiptMutation,
  reanalyzeReceiptMutation,
  receiptQuery,
} from "@/app/Refunds/GraphQL/refundMutations";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Link } from "@tanstack/react-router";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { EditReceiptDialog } from "@/app/Refunds/Components/EditReceiptDialog";
import {
  PencilIcon,
  ArrowPathIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { ConfirmDeleteDialog } from "@/lib/Components/Dialog/ConfirmDeleteDialog";
import { toast } from "sonner";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

interface ReportReceiptsTableProps {
  ref: any;
  startDate: string;
  endDate: string;
}

function ReceiptImageDialog({ receiptId }: { receiptId: string }) {
  return (
    <BaseDialog title="Receipt Image" size="2xl">
      <Suspense fallback={<Spinner />}>
        <ReceiptImageContent receiptId={receiptId} />
      </Suspense>
    </BaseDialog>
  );
}

function ReceiptImageContent({ receiptId }: { receiptId: string }) {
  const { data } = useSuspenseGqlQuery(receiptQuery, { id: receiptId });
  const image = data.receipt?.image;

  if (!image) {
    return (
      <div className="flex items-center justify-center rounded-lg border bg-zinc-50 p-12">
        <p className="text-sm text-zinc-500">No image attached</p>
      </div>
    );
  }

  if (image.mime_type === "application/pdf") {
    return (
      <iframe
        src={image.url}
        title="Receipt"
        className="h-[70vh] w-full rounded-lg border"
      />
    );
  }

  return (
    <div className="flex items-center justify-center">
      <ImoovaImage
        media={image}
        alt="Receipt"
        className="max-h-[70vh] w-auto rounded-lg"
      />
    </div>
  );
}

function ReceiptActionsCellRenderer({
  data,
  node,
}: ICellRendererParams<ReportReceiptItem>) {
  const { open: openEditReceiptDialog } = useDialog(EditReceiptDialog);
  const { open: openImageDialog } = useDialog(ReceiptImageDialog);
  const { open: openDeleteDialog } = useDialog(ConfirmDeleteDialog);
  const { mutateAsync: reanalyzeReceipt } = useGqlMutation(
    reanalyzeReceiptMutation,
  );
  const { mutateAsync: deleteReceipt } = useGqlMutation(deleteReceiptMutation);
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  if (!data?.id || node?.isRowPinned()) return null;

  const id = String(data.id);

  async function handleReanalyze() {
    setIsReanalyzing(true);
    try {
      await reanalyzeReceipt({ id });
      toast.success("Receipt reanalyzed");
    } catch {
      toast.error("Failed to reanalyze receipt");
    } finally {
      setIsReanalyzing(false);
    }
  }

  return (
    <div className="flex size-full items-center gap-1">
      <button
        type="button"
        onClick={() => openImageDialog({ receiptId: id })}
        title="View image"
        className="text-zinc-400 hover:text-zinc-700"
      >
        <PhotoIcon className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => openEditReceiptDialog({ receiptId: id })}
        title="Edit receipt"
        className="text-zinc-400 hover:text-zinc-700"
      >
        <PencilIcon className="size-4" />
      </button>
      <button
        type="button"
        onClick={handleReanalyze}
        disabled={isReanalyzing}
        title="Reanalyze receipt"
        className="text-zinc-400 hover:text-zinc-700 disabled:pointer-events-none disabled:text-blue-500"
      >
        <ArrowPathIcon
          className={`size-4 ${isReanalyzing ? "animate-spin" : ""}`}
        />
      </button>
      <button
        type="button"
        onClick={() =>
          openDeleteDialog({
            title: "Delete receipt",
            message:
              "Are you sure you want to delete this receipt? This action cannot be undone.",
            onDelete: async () => {
              try {
                await deleteReceipt({ id });
                toast.success("Receipt deleted");
              } catch {
                toast.error("Failed to delete receipt");
              }
            },
          })
        }
        title="Delete receipt"
        className="text-zinc-400 hover:text-red-600"
      >
        <TrashIcon className="size-4" />
      </button>
    </div>
  );
}

export function ReportReceiptsTable({
  ref,
  startDate,
  endDate,
}: ReportReceiptsTableProps) {
  const { data, isLoading, isError } = useGqlQuery(reportReceiptsQuery, {
    rangeStartDate: startDate,
    rangeEndDate: endDate,
  });

  const formatAmount = useCallback((params: any) => {
    return formatCurrency(params.value, Currency.Aud, {
      emptyValue: "",
    });
  }, []);

  const columns = useMemo(() => {
    return [
      {
        headerName: "",
        width: 140,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: ReceiptActionsCellRenderer,
      },
      {
        field: "id",
        headerName: "ID",
        filter: "agTextColumnFilter",
        width: 80,
      },
      {
        field: "date",
        headerName: "Date",
        valueGetter: (params) => {
          if (!params.data?.date) return null;
          return dayjs(params.data.date).toDate();
        },
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("DD/MM/YYYY");
        },
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
            if (!cellValue) return -1;
            const normalizedFilterDate = dayjs(
              filterLocalDateAtMidnight,
            ).startOf("day");
            const normalizedCellDate = dayjs(cellValue).startOf("day");

            if (normalizedFilterDate.isBefore(normalizedCellDate, "day"))
              return 1;
            if (normalizedFilterDate.isAfter(normalizedCellDate, "day"))
              return -1;
            return 0;
          },
        },
      },
      {
        field: "type",
        headerName: "Type",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => {
          if (!params.value) return "";
          return params.value.replace(/_/g, " ");
        },
      },
      {
        field: "related_reference",
        headerName: "Related To",
        filter: "agTextColumnFilter",
        cellRenderer: (params: ICellRendererParams<ReportReceiptItem>) => {
          if (!params.data?.related_reference || !params.data?.related_id) {
            return null;
          }
          const type = params.data.related_type;
          const id = params.data.related_id;

          if (type === "BOOKING") {
            return (
              <Link
                to="/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
                params={(prev) => ({ ...prev, bookingId: id })}
                className="text-blue-600 hover:underline"
              >
                {params.data.related_reference}
              </Link>
            );
          }

          if (type === "WORK_ORDER") {
            return (
              <Link
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
                params={(prev) => ({ ...prev, id })}
                className="text-blue-600 hover:underline"
              >
                {params.data.related_reference}
              </Link>
            );
          }

          return params.data.related_reference;
        },
      },
      {
        field: "description",
        headerName: "Description",
        filter: "agTextColumnFilter",
        flex: 2,
      },
      {
        field: "amount_inc_tax",
        context: { isCurrency: true },
        headerName: "Amount (Inc GST)",
        aggFunc: "sum",
        valueFormatter: (params) => formatAmount(params),
      },
      {
        headerName: "Claimable %",
        width: 120,
        valueGetter: (params) => {
          if (!params.data?.amount_inc_tax) return null;
          return Math.round(
            (params.data.claimable_amount_inc_tax /
              params.data.amount_inc_tax) *
              100,
          );
        },
        valueFormatter: (params) => {
          if (params.value == null) return "";
          return `${params.value}%`;
        },
      },
      {
        field: "claimable_amount_inc_tax",
        context: { isCurrency: true },
        headerName: "Claimable (Inc GST)",
        aggFunc: "sum",
        valueFormatter: (params) => formatAmount(params),
      },
      {
        field: "claimable_amount_tax",
        context: { isCurrency: true },
        headerName: "Claimable GST",
        aggFunc: "sum",
        valueFormatter: (params) => formatAmount(params),
      },
    ] as ColDef<ReportReceiptItem>[];
  }, [formatAmount]);

  const getPinnedBottomRowData = useCallback(() => {
    if (!data?.reportReceipts?.totals) {
      return [];
    }

    const { totals } = data.reportReceipts;

    return [
      {
        description: `Total Receipts: ${totals.total_receipts}`,
        amount_inc_tax: totals.total_amount_inc_tax,
        claimable_amount_inc_tax: totals.total_claimable_amount_inc_tax,
        claimable_amount_tax: totals.total_claimable_amount_tax,
        _isSummaryRow: true,
      },
    ];
  }, [data?.reportReceipts?.totals]);

  const getRowStyle = useCallback((params: any) => {
    if (params.data?._isSummaryRow) {
      return {
        backgroundColor: "#f3f4f6",
        fontWeight: "600",
      };
    }
    return undefined;
  }, []);

  if (isError) {
    return <div>An error occurred while retrieving report</div>;
  }

  return (
    <AgGridReact<ReportReceiptItem>
      className="grow"
      ref={ref}
      rowData={data?.reportReceipts?.receipts ?? []}
      loading={isLoading}
      theme={imoovaTheme}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
      pinnedBottomRowData={getPinnedBottomRowData()}
      getRowStyle={getRowStyle}
      getRowId={({ data }) => String(data.id ?? "summary")}
    />
  );
}
